// Live-league hero — pure media resolver (HS1).
//
// Maps STRIKZA league_code → the LICENSED API-Football media CDN
// (we subscribe to api-sports.io; these URLs are hot-linkable under
// that plan — no scraping, no stored image bytes, no facial data).
// Pure: code in, URL or null out. A null return is HONEST — the
// caller must render a monogram fallback, never a fabricated/stock
// graphic standing in for the real crest or player.
//
// Player headshots are addressed by the provider's numeric player
// id (sourced ONLY from the budget-gated lineup ingest, never
// scraped). monogram() gives the deterministic honest fallback.

/** api-sports.io media CDN root (football vertical). */
const MEDIA_ROOT = "https://media.api-sports.io/football";

/** STRIKZA league_code → API-Football v3 league id. Top-6 are the
 *  genuinely-live budgeted competitions; the Tier-1 blocks are
 *  registered for when a later gate activates them (still dark until
 *  then — this map never makes a league live, it only resolves art
 *  for whatever the gate already reports as live). Mirrors the ids
 *  in providers/api-football.ts and _api_football_league_metadata. */
export const LEAGUE_API_FOOTBALL_ID: Readonly<Record<string, number>> = {
  // Live top-6 (confirmed slugs via _api_football_league_metadata).
  "eng-premier-league": 39,
  "esp-la-liga": 140,
  "ita-serie-a": 135,
  "ger-bundesliga": 78,
  "fra-ligue-1": 61,
  "uefa-champions-league": 2,
  // Tier-1 Block A (registered, gate-dark until activated).
  "sco-premiership": 179,
  "ned-eredivisie": 88,
  "por-primeira-liga": 94,
  "tur-super-lig": 203,
  "bel-pro-league": 144,
  "nor-eliteserien": 103,
  "den-superliga": 119,
  "gre-super-league": 197,
  // Tier-1 Block B.
  "aut-bundesliga": 218,
  "sui-super-league": 207,
  "eng-championship": 40,
  "eng-league-one": 41,
  "eng-league-two": 42,
  "ger-2-bundesliga": 79,
  "ita-serie-b": 136,
  "fra-ligue-2": 62,
};

/** Licensed league-badge URL, or null when the code is unmapped
 *  (caller renders the monogram — never a fabricated badge). */
export function leagueBadgeUrl(leagueCode: string | null | undefined): string | null {
  if (!leagueCode) return null;
  const id = LEAGUE_API_FOOTBALL_ID[leagueCode];
  return typeof id === "number" ? `${MEDIA_ROOT}/leagues/${id}.png` : null;
}

/** Licensed player-headshot URL by provider player id. null when
 *  the id is absent/invalid — caller MUST monogram-fallback (we
 *  never invent a face). Not derivable from a name; the id only
 *  ever comes from the budget-gated lineup ingest. */
export function playerHeadshotUrl(
  playerId: number | string | null | undefined,
): string | null {
  if (playerId === null || playerId === undefined) return null;
  const n = typeof playerId === "string" ? Number(playerId) : playerId;
  if (!Number.isInteger(n) || n <= 0) return null;
  return `${MEDIA_ROOT}/players/${n}.png`;
}

/** Deterministic 1–3 char monogram for the honest fallback tile
 *  (used when a licensed asset is unavailable). Letters only, upper. */
export function monogram(name: string | null | undefined): string {
  if (!name) return "—";
  const words = name
    .replace(/[^A-Za-z\s]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "—";
  if (words.length === 1) return words[0]!.slice(0, 3).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}
